let _types = [];
let _data = [];

import {d3} from './Util/d3-sankey';
import journeyBuilderApi from "../../../api";

const getTypes = () => {   
    return _types;
};

const getData = () => {
    return _data;
};

const initGraph = (types, data) => {
    _types = types;
    _data = data;
};

const render = (cssSelector) => {
    renderGraph(cssSelector, getFormattedData);
};

const calculateNodeHorizontalPositions = (nodes, nodeIdMap) => {
    return nodes.reduce((nodePositionsMap, node) => {
        const processed = [];

        let position = 0;
        let currentNode = nodeIdMap[node.from];

        while ( currentNode && processed.indexOf(currentNode.id) === -1 ){
            position++;
            processed.push(currentNode.id);

            currentNode = nodeIdMap[currentNode.from];
        }
        
        nodePositionsMap[node.id] = position;

        return nodePositionsMap;
    }, {});
};

const calculateTypeVerticalPositions = () => {
    return getTypes().reduce((positionMap, type, idx) => {
        positionMap[type.name] = idx;
        return positionMap;
    }, {});
};

const getFormattedData = () => {
    const nodeTypeVerticalPositionsMap = calculateTypeVerticalPositions();
    
    //Ensure our nodes are sorted prior to assigning ids
    const rawData = getData();
    
    //Ensure all types have a start node
    const startNodeTypes = rawData.filter(node => !node.from)
        .map(node => node.type);

    getTypes().map(t => t.name).forEach(typeName => {
        if(startNodeTypes.indexOf(typeName) !== -1) {
            return;
        }
        
        rawData.push({
            id: `_Start_${typeName}`,
            from: undefined,

            type: typeName,
            total: 0
        });
    });
    
    
    const graphData = _.orderBy(rawData, node => nodeTypeVerticalPositionsMap[node.type], 'asc');
    
    //Within Sankey; our nodes are simply identified as a number
    const {nodeIndexMap, nodeIdMap} = graphData.reduce((nodeMap, node, idx) => {
        nodeMap.nodeIndexMap[node.id] = idx;
        nodeMap.nodeIdMap[node.id] = node;
        
        return nodeMap;
    }, { nodeIndexMap: {}, nodeIdMap: {} });

    const nodeHorizontalPositionsMap = calculateNodeHorizontalPositions(graphData, nodeIdMap);
    
    const nodes = graphData.map(node => ({
        //Required by sankey plugin
        node: nodeIndexMap[node.id],
        name: node.id,
        
        //For internal use by us
        _position: {
            x: nodeHorizontalPositionsMap[node.id],
            y: nodeTypeVerticalPositionsMap[node.type]
        },
        _stripey: node.stripey,
        _type: node.type,
        _color: node.color,
        _brickId: node.brickId,
        _isBottomNode: false
    }));

    //Our horizontal legend is rendered on the bottom
    //thus we need to keep track of which nodes are on the absolute bottom
    const bottomNodes = nodes.reduce((bottomNodes, node) => {
        const x = node._position.x;
        
        if(!bottomNodes.hasOwnProperty(x) || node._position.y > bottomNodes[x]._position.y){
            bottomNodes[x] = node;
        }
        
        return bottomNodes;
    }, { });
    
    Object.values(bottomNodes).forEach(node => node._isBottomNode = true);
    
    const links = graphData.reduce((links, node) => {
        if(node.from){
            links.push({
                source: nodeIndexMap[node.from],
                target: nodeIndexMap[node.id],
                value: node.total
            });
        }
        
        return links;
    }, []);
    
    return {
        nodes: nodes,
        links: links
    };
};

const getPositionText = position => {
    switch (position) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'd';
        default: return 'th';
    }
};

const calculateNodeDistance = () => {
    const totalTypes = getTypes().length;
    
    switch (totalTypes) {
        case 2:
            //2 nodes require a greater distance between them to prevent
            //the curve from curving behind the node
            return 250;
        case 3:
            //Untested; might not be needed
            return 200;
        default:
            return 150;
    }
};

const renderGraph = (cssSelector, dataSourceFn) => {
    const graph = dataSourceFn();
    const element = document.querySelector(cssSelector);
    
    const maxXAxis = Math.max(...graph.nodes.map(n => n._position.x));
    const marginLeft = 150;
    const nodeDistance = calculateNodeDistance();
    
    //nodeWidth * X + margin left
    const minGraphWidth = (maxXAxis * nodeDistance) + nodeDistance;
    
    //Based on:
    //https://bl.ocks.org/d3noob/013054e8d7807dff76247b81b0e29030

    const margin = { left: marginLeft };
    
    const boundingBox = element.getBoundingClientRect();
    const width = Math.max(boundingBox.width, minGraphWidth);
    
    const height = Math.max(boundingBox.height, 500);

    const sankeyBottomPadding = 20;
    const sankeyBottomLabelMargin = 10;

    //Take away some height to ensure our custom elements fit
    const sankeyHeight = height - sankeyBottomPadding;

    // append the svg object to the body of the page
    var svg = d3.select(cssSelector).append("svg")
        .attr("width", width)
        .attr("height", height)
        .append("g");

    //https://stackoverflow.com/questions/17776641/fill-rect-with-pattern
    const stripeBackgroundId = 'diagonalHatch';
    const stripeBackgroundIdUrl = `url(#${stripeBackgroundId})`;
    
    svg
        .append('defs')
        .append('pattern')
        .attr('id', stripeBackgroundId)
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 4)
        .attr('height', 4)
        .append('path')
        .attr('d', 'M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2')
        .attr('stroke', '#000000')
        .attr('stroke-width', 1);

    // Set the sankey diagram properties
    var sankey = d3.sankey()
        .nodeWidth(10)
        .nodePadding(nodeDistance)
        .size([
            width - margin.left,
            sankeyHeight
        ]);
    

    var path = sankey.link();
    var maxValue=0;
    var nodeMap = {};

    _data.forEach(function(value) {
        maxValue = Math.max(maxValue, value.total);
        nodeMap[value.id] = {value: value.total};
    });

    sankey
        .nodes(graph.nodes)
        .links(graph.links)
        .layout(32);

    //Sankey assigns incorrect values to our nodes; 
    //we'll have to swoop in and fix those ourselves post layout
    graph.nodes.forEach(node => {
        const originalNode = nodeMap[node.name];
        
        if(originalNode){
            node.value = originalNode.value;
        }
    });
    
    const types = getTypes();

    //Value only becomes available after having invoked sankey
    const heightPerType = Math.floor(sankeyHeight / types.length);

    const typeColorMap = types.reduce((colorMap, type) => { 
        colorMap[type.name] = type.color;
        return colorMap;
    }, {});

    //Evenly distribute height per type based value compared to max value
    const calculateNodeHeight = d => (d.value / maxValue) * heightPerType;
    const calculateNodeWidth = d => sankey.nodeWidth();
    const calculateNodeY = d => d._position.y * heightPerType;
    const calculateNodeVerticalAxisLegendY = d => calculateNodeY(d) + 10; // heightPerType / 2;
    const calculateNodeHorizontalAxisLegendY = () => (types.length * heightPerType) + sankeyBottomLabelMargin;
    const calculateNodeX = d => (d._position.x * nodeDistance) + margin.left;
    
    //Prefer embedded node color over type color
    const calculateNodeColor = d => d._color || typeColorMap[d._type];
    
    const calculateLinkWidth = function(d) {
        const sourceNode = nodeMap[d.source.name];
        const strokeWidth = (d.value / sourceNode.value) * calculateNodeHeight(sourceNode);
        
        //Empty nodes should not have a link drawn
        if(d.value === 0){
            return 0;
        }
        
        //Ensure our link is always visible by declaring a minimum width of 1
        return Math.max(strokeWidth, 1);
    };

    for(let i=0;i<graph.links.length;i++) {
        graph.links[i].link_index = i;
    }

    const prevMap = {};
    // add in the links
    svg.append('g')
        .selectAll('.link')
        .data(graph.links)
        .enter()
        .append('path')
        .attr('class', 'link')
        .attr('d', d => {

            //Calculate starting y based on previous link if still on the same node, else maxheight * previous nodes.
            prevMap[d.source.name] = prevMap[d.source.name] || d.source._position.y * heightPerType;
            const sourceX = calculateNodeX(d.source);

            const targetX = calculateNodeX(d.target);
            let targetY = calculateNodeY(d.target);
            
            const strokeWidth = calculateLinkWidth(d);
             
            const sourceY = prevMap[d.source.name];
            prevMap[d.source.name] = sourceY + strokeWidth;
            
            d._ssx = sourceX;
            d._ssy = sourceY;
            d._sw = strokeWidth;
            
            //Seems to set the Y for our connector
            //const sy =  ((d.sy / height) * heightPerType);

            //Seems to do... something related to target position of our connector
            //does nothing when set to 0/sy
            const ty = ((d.ty / sankeyHeight) * heightPerType);
            
            // Seems to set the base Y for our connector
            const dy = strokeWidth;
            
            const fakeLink = {
                //Sy = line Y origin
                
                sy:  0,//sy, //LineSourceY
                ty:  ty, //LineTargetY
                dy: dy, //d... Y?
                source: {
                    x: sourceX,
                    dx: d.source.dx,
                    y: sourceY
                },
                target: {
                    x: targetX,
                    y: targetY
                }
            };
            
            return path(fakeLink);
        })
        .style('stroke-width', calculateLinkWidth)
        .sort((a, b) => b.dy - a.dy)
        .append("title")
        .text(d => `${d.source._type} - ${d.value}`);
    
    // add in the nodes
    var node = svg.append("g")
        .selectAll(".node")
        .data(graph.nodes)
        .enter()
        .append("g")
        .attr("class", "node")
        .attr('transform', d => `translate(${calculateNodeX(d)}, 1)`);
    
    // add the rectangles for the nodes
    node.append("rect")
        .style("fill", calculateNodeColor)
        .style("stroke", d => d3.rgb(calculateNodeColor(d)).darker(2));

    //Mark stripey nodes... stripey
    //stripey should come after the initial fill
    //otherwise the stripes will be a slightly different shade of
    //fill color
    node.filter(d => d._stripey)
        .append('rect')
        .attr("height", calculateNodeHeight)
        .attr("width", calculateNodeWidth)
        .attr('y', calculateNodeY)
        .style('fill', stripeBackgroundIdUrl);
    
    node.selectAll('rect')
        //Evenly distribute height per type based value compared to max value
        .attr("height", calculateNodeHeight)
        .attr("width", calculateNodeWidth)
        .attr('y', calculateNodeY)
        .on('click', function(d){
            
            const event = new CustomEvent('nodeClick', {
                bubbles: true,
                detail: {
                    brickId: d._brickId
                }
            });
            
            this.dispatchEvent(event);
            
            // window.open(journeyBuilderApi.getViewTouchPointStatisticsUrl(d._brickId), '_blank');

        })
        .append("title")
        .text(d => d._type);
    
    //Create our Y axis 'legend', one item per left most node
    node.filter(d => d._position.x === 0)
        .append('text')
        .attr("x", -100)
        .attr("y", calculateNodeVerticalAxisLegendY) //Min 1 to prevent text from being cut off 
        .attr("text-anchor", "end")
        .attr("transform", null)
        .text(d => d._type)
        .attr("text-anchor", "start");

    //Create our X axis 'legend', one item per bottom most node, excluding our entry point
    //our entry points are defined by our Y axis legend
    node.filter(d => d._position.x !== 0 && d._isBottomNode)
        .append('text')
        .attr('x', 0)
        .attr('y', calculateNodeHorizontalAxisLegendY)
        .attr('text-anchor', 'end')
        .attr('transform', null)
        .text(d => { 
            const touchpointNr = d._position.x;
            return `${touchpointNr}${getPositionText(touchpointNr)} touch`;
        })
        .attr("text-anchor", "start");
};


export default {
    initGraph,
    render
};